import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short'
  });
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const readingTime = data.fullDate.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return (
    <div className="bg-white p-4 rounded shadow-lg border border-gray-200">
      <p className="font-medium text-gray-800">{label} at {readingTime}</p>
      <p className="text-red-600">Blood Pressure: {data.systolic}/{data.diastolic} mmHg</p>
      <div className="mt-2 text-sm text-gray-600">
        <p>Systolic: {data.systolic} mmHg</p>
        <p>Diastolic: {data.diastolic} mmHg</p>
      </div>
    </div>
  );
};

const BloodPressureChart = ({ entries, dateRange }) => {
  const bloodPressureData = entries
    .filter(entry => entry.type === 'bloodPressure')
    .filter(entry => {
      if (!dateRange.start && !dateRange.end) return true;
      
      const entryDate = new Date(entry.datetime);
      const startDate = dateRange.start ? new Date(dateRange.start) : null;
      const endDate = dateRange.end ? new Date(dateRange.end) : null;
      
      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999);
      
      if (startDate && entryDate < startDate) return false;
      if (endDate && entryDate > endDate) return false;
      return true;
    })
    .map(entry => {
      const entryDate = new Date(entry.datetime);
      // Parse blood pressure value (e.g., "120/80")
      const [systolic, diastolic] = entry.value.split('/').map(v => parseInt(v.trim()));
      
      return {
        fullDate: entryDate,
        date: formatDate(entry.datetime),
        systolic: systolic || 0,
        diastolic: diastolic || 0,
        value: entry.value
      };
    })
    .filter(entry => entry.systolic > 0 && entry.diastolic > 0) // Filter out invalid readings
    .sort((a, b) => a.fullDate - b.fullDate);

  if (bloodPressureData.length === 0) {
    return (
      <div className="h-[400px] flex items-center justify-center text-gray-500">
        No blood pressure data available for the selected period
      </div>
    );
  }

  const allValues = bloodPressureData.flatMap(d => [d.systolic, d.diastolic]);
  const minValue = Math.floor(Math.min(...allValues));
  const maxValue = Math.ceil(Math.max(...allValues));
  const lowerBound = Math.max(minValue - 10, 40);  // 10 units below lowest reading, but not lower than 40
  const upperBound = Math.min(maxValue + 10, 200);  // 10 units above highest reading, but not higher than 200

  return (
    <div className="h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={bloodPressureData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="date" 
            angle={-45}
            textAnchor="end"
            height={70}
          />
          <YAxis 
            label={{ value: 'mmHg', angle: -90, position: 'insideLeft' }}
            domain={[lowerBound, upperBound]}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area
            type="monotone"
            dataKey="systolic"
            stackId="1"
            stroke="#dc2626"
            fill="#dc2626"
            fillOpacity={0.3}
          />
          <Area
            type="monotone"
            dataKey="diastolic"
            stackId="1"
            stroke="#dc2626"
            fill="#dc2626"
            fillOpacity={0.6}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BloodPressureChart;
